// NewsletterButton.js
import React, { useState } from 'react';
import { Mail, Bell } from 'lucide-react';

const NewsletterButton = ({ onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const handleClick = () => {
    setIsClicked(true);
    onClick();
    setTimeout(() => setIsClicked(false), 200);
  };

  return (
    <div style={styles.container}>
      {/* Notification Badge */}
      <div style={styles.badge}>
        <span style={styles.badgeText}>New</span>
      </div>
      
      {/* Main Button */}
      <button
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          ...styles.button,
          transform: isHovered 
            ? 'scale(1.1)' 
            : isClicked 
              ? 'scale(0.95)' 
              : 'scale(1)',
          boxShadow: isHovered 
            ? '0 12px 30px rgba(102, 126, 234, 0.6)' 
            : '0 8px 20px rgba(102, 126, 234, 0.4)',
        }}
      >
        {/* Background Circles for depth */}
        <div style={styles.backgroundCircle1}></div>
        <div style={styles.backgroundCircle2}></div>
        
        {/* Icon */}
        <Mail 
          size={24} 
          color="white" 
          style={{
            transform: isHovered ? 'rotate(10deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
        
        {/* Pulse Ring */}
        <div style={{
          ...styles.pulseRing,
          animation: isHovered ? 'pulse 2s infinite' : 'none',
        }} />
      </button>
      
      {/* Tooltip */}
      {isHovered && (
        <div style={styles.tooltip}>
          <span>Subscribe to Newsletter</span>
          <div style={styles.tooltipArrow}></div>
        </div>
      )}
    </div>
  );
};

// Styles for Newsletter Button
const styles = {
  container: {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    zIndex: 999,
  },
  badge: {
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    backgroundColor: '#ff4757',
    borderRadius: '12px',
    padding: '4px 8px',
    zIndex: 2,
    animation: 'bounce 2s infinite',
  },
  badgeText: {
    color: 'white',
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  button: {
    position: 'relative',
    width: '64px',
    height: '64px',
    backgroundColor: '#667eea',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    outline: 'none',
    overflow: 'hidden',
  },
  backgroundCircle1: {
    position: 'absolute',
    width: '120%',
    height: '120%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    top: '-10%',
    left: '-10%',
  },
  backgroundCircle2: {
    position: 'absolute',
    width: '80%',
    height: '80%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    top: '10%',
    left: '10%',
  },
  pulseRing: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    border: '3px solid #667eea',
    opacity: 0.6,
  },
  tooltip: {
    position: 'absolute',
    bottom: '75px',
    right: '0',
    backgroundColor: '#2d3748',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    animation: 'fadeInUp 0.3s ease-out',
  },
  tooltipArrow: {
    position: 'absolute',
    top: '100%',
    right: '16px',
    width: 0,
    height: 0,
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    borderTop: '6px solid #2d3748',
  },
};

// CSS Animations for Button
const buttonCSS = `
  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0.6;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.3);
      opacity: 0.3;
    }
    100% {
      transform: translate(-50%, -50%) scale(1.6);
      opacity: 0;
    }
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-8px);
    }
    60% {
      transform: translateY(-4px);
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'newsletter-button-styles';
  styleSheet.textContent = buttonCSS;
  if (!document.getElementById('newsletter-button-styles')) {
    document.head.appendChild(styleSheet);
  }
}

export default NewsletterButton;